/* eslint-disable */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { EXCEL_STYLES, DEFAULT_LOGO } from './constants.js';
import { safeFileName } from './utils/files.js';
import { sortColumnsByOrder, findColumnIndexByKey } from './utils/columns.js';
import { createHeaderStyle } from './utils/colors.js';
import { applyCellStyle } from './utils/excel-helpers.js';
import { detectDataFormat } from './data/format-detector.js';
import { getMaxNestingLevelGroups, getMaxNestingLevelWBS } from './data/nesting-level.js';
import { processNestedGroups, processWBSGroups, processFlatTasks } from './data/task-processors.js';
import { calculateAggregationFromData, getTotalAggregationValue } from './data/aggregation.js';
import { createFieldHandlers } from './handlers/field-handlers.js';
import { 
  createWorksheetWBSFormat, 
  createWorksheetFlatFormat, 
  createWorksheetGroupedFormat 
} from './excel/worksheet-creators.js';

export class ExcelExporter {
  constructor(options = {}) {
    this.options = {
      logo: options.logo || false,
      logoImg: options.logoImg || DEFAULT_LOGO,
      headBannerRowSize: options.headBannerRowSize || 3,
      subHeaderText: options.subHeaderText || 'Report',
      colorMap: options.colorMap || {},
      constants: options.constants || {},
      enableLevel: options.enableLevel || false,
      companyName: options.companyName || '',
      dateFormat: options.dateFormat || 'dd/mm/yyyy',
      logoWidth: options.logoWidth || 240,
      logoHeight: options.logoHeight || 60,
      logoColumns: options.logoColumns || 2,
      footerText: options.footerText || 'Generated on {date}',
      defaultFileExtension: options.defaultFileExtension || 'xlsx',
      includeFooter: options.includeFooter !== false,
      includeAggregation: options.includeAggregation || false,
      aggregationData: options.aggregationData || [],
      autoCalculateAggregation: options.autoCalculateAggregation !== false,
      showTotalAggregation: options.showTotalAggregation !== false
    };
    
    this.fieldHandlers = createFieldHandlers(this.options.colorMap);
  }

  getAggregationData(processedTasks, columns, dataFormat) {
    const { includeAggregation, aggregationData, autoCalculateAggregation, showTotalAggregation } = this.options;
    
    if (!includeAggregation) return [];
    if (aggregationData && aggregationData.length > 0) return aggregationData;
    
    if (autoCalculateAggregation) {
      const includeTotal = showTotalAggregation || 
        ['grouped', 'nested-group', 'wbs'].includes(dataFormat);
      return calculateAggregationFromData(processedTasks, columns, includeTotal);
    }
    
    return [];
  }

  getTaskId(task) {
    if (task.isGroupHeader) return task.groupId;
    return task.data?.id || task.data?.ID || null;
  }

  getTaskData(task, columnKey) {
    if (!task.data) return '';
    if (task.isGroupHeader) {
      if (columnKey === 'name' || columnKey === 'title') return task.title;
      return task.data[columnKey] || '';
    }
    return task.data[columnKey] || '';
  }

  prepareColumns(columns, addLevelColumn = true, dataFormat = 'grouped') {
    const sortedColumns = sortColumnsByOrder(columns);
    const flatColumns = [];
    const columnHeaders = [];
    
    if (dataFormat === 'wbs') {
      flatColumns.push({ key: 'wbs', label: 'WBS', type: 'wbs', colWidth: 15 });
      columnHeaders.push({ value: 'WBS', style: createHeaderStyle(this.options.colorMap) });
    }
    
    sortedColumns.forEach(column => {
      flatColumns.push(column);
      columnHeaders.push({ 
        value: column.label || '', 
        style: createHeaderStyle(this.options.colorMap) 
      });
    });
    
    if (addLevelColumn) {
      flatColumns.push({ key: 'level', label: 'Level', type: 'level', colWidth: 8 });
      columnHeaders.push({ value: __('excel_level'), style: createHeaderStyle(this.options.colorMap) });
    }
    
    return { flatColumns, columnHeaders };
  }

  processTasks(tasks, dataFormat = 'grouped') {
    switch (dataFormat) {
      case 'wbs': return processWBSGroups(tasks);
      case 'flat': return processFlatTasks(tasks);
      case 'nested-group':
      case 'grouped':
      default: return processNestedGroups(tasks);
    }
  }

  shouldAddTotalAggregation(aggregationData) {
    return this.options.includeAggregation && aggregationData.some(item => item.id == 0);
  }

  addTotalAggregationRow(worksheet, currentRow, flatColumns, maxNestingLevel, aggregationData) {
    const aggregationRow = worksheet.getRow(currentRow);
    const borderColor = EXCEL_STYLES.GRID_BORDER;
    let currentCol = 1;
    
    for (let i = 0; i < maxNestingLevel; i++) {
      const cell = aggregationRow.getCell(currentCol);
      cell.value = '';
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_STYLES.AGGREGATION_BG }
      };
      currentCol++;
    }
    
    flatColumns.forEach((column, index) => {
      const cell = aggregationRow.getCell(currentCol);
      const totalValue = getTotalAggregationValue(column.key, aggregationData);
      
      if (totalValue !== null && column.type === 'aggregation') {
        const handler = this.fieldHandlers[column.type] || this.fieldHandlers.default;
        const result = handler(totalValue, column);
        cell.value = result.value || '';
        
        if (result.style) {
          result.style.font = { ...(result.style.font || {}), bold: true };
          applyCellStyle(cell, result.style);
        } else {
          cell.font = { ...(cell.font || {}), bold: true };
        }

        cell.border = { // Добавляем бордеры для ячейки агрегации
          bottom: { style: 'thin', color: { argb: borderColor } },
          right: { style: 'thin', color: { argb: borderColor } },
          left: { style: 'thin', color: { argb: borderColor } },
        }
      } else {
        cell.value = '';
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: EXCEL_STYLES.AGGREGATION_BG }
        };

        cell.border = {
          bottom: { style: 'thin', color: { argb: borderColor } },
        }
    
        if (index === flatColumns.length - 1) { // Добавляем бордер справа для крайней парвой ячейки
          cell.border = {
            bottom: { style: 'thin', color: { argb: borderColor } },
            right: { style: 'thin', color: { argb: borderColor } },
          }
        }
      }
      
      currentCol++;
    });
  }

  async exportToExcel(exportData, fileExtension = null) {
    const { fileName = 'export', columns = [], tasks = [], worksheetName = 'Sheet1' } = exportData;
    
    if (!Array.isArray(columns) || columns.length === 0) throw new Error('Columns must be a non-empty array');
    if (!Array.isArray(tasks) || tasks.length === 0) throw new Error('Tasks must be a non-empty array');
    
    const extension = fileExtension || this.options.defaultFileExtension || 'xlsx';
    const workbook = new ExcelJS.Workbook();
    workbook.calcProperties.fullCalcOnLoad = true;
    
    this.createWorksheet(workbook, worksheetName, columns, tasks);
    
    const safeName = safeFileName(fileName, true);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, `${safeName}.${extension}`);
    return safeName;
  }

  createWorksheet(workbook, worksheetName, columns, tasks) {
    const worksheet = workbook.addWorksheet(safeFileName(worksheetName, false));
    
    const dataFormat = detectDataFormat(tasks);
    const { flatColumns, columnHeaders } = this.prepareColumns(columns, this.options.enableLevel, dataFormat);
    const processedTasks = this.processTasks(tasks, dataFormat);
    const aggregationData = this.getAggregationData(processedTasks, flatColumns, dataFormat);
    
    const maxNestingLevel = dataFormat === 'wbs' 
      ? getMaxNestingLevelWBS(tasks)
      : getMaxNestingLevelGroups(tasks);
    
    switch (dataFormat) {
      case 'wbs':
        return createWorksheetWBSFormat(this, worksheet, workbook, worksheetName, flatColumns, columnHeaders, processedTasks, maxNestingLevel, aggregationData);
      case 'flat':
        return createWorksheetFlatFormat(this, worksheet, workbook, worksheetName, flatColumns, columnHeaders, processedTasks, aggregationData);
      case 'nested-group':
      case 'grouped':
      default:
        return createWorksheetGroupedFormat(this, worksheet, workbook, worksheetName, flatColumns, columnHeaders, processedTasks, maxNestingLevel, aggregationData);
    }
  }
}